import React, { useEffect, useState, useCallback } from "react";
import Modal from "react-modal";
import {
  collection,
  getDocs,
  doc,
  onSnapshot,
  query,
  where,
} from "firebase/firestore";
import { ref, getDatabase, get } from "firebase/database";
import { db } from "../../firebase";
import { Spin } from "antd";
import { SEND_NOTIFICATION, COLLECTION_NAME } from "../../constants/endpoints";

Modal.setAppElement("#root");

const AssignDriverModal = ({
  isOpen,
  onRequestClose,
  ride,
  onVerify,
  isAssigning,
}) => {
  console.log("Ride data in AssignDriverModal:", ride);
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [existingRides, setExistingRides] = useState([]);
  const [checkingRides, setCheckingRides] = useState(false);

  const filteredDrivers = drivers.filter((driver) =>
    `${driver.fullName} ${driver.phoneNumber}`
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const fetchActiveRides = useCallback(async () => {
    try {
      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);
      const snapshot = await get(ridesRef);

      if (snapshot.exists()) {
        const ridesData = snapshot.val();
        const activeRiderIds = [];

        // Extract riderIds from active rides
        Object.keys(ridesData).forEach((rideId) => {
          const ride = ridesData[rideId];
          // Check if ride is active (you can adjust these statuses based on your app logic)
          if (
            ride.status &&
            ["accepted"].includes(ride.status) &&
            ride.riderId
          ) {
            activeRiderIds.push(ride.riderId);
          }
        });

        return activeRiderIds;
      }
      return [];
    } catch (error) {
      console.error("Error fetching active rides:", error);
      return [];
    }
  }, []);

  const fetchDrivers = useCallback(async () => {
    try {
      console.log("Ride is here top:", ride);
      // First fetch active rides to get rider IDs
      const activeRiderIds = await fetchActiveRides();

      const querySnapshot = await getDocs(
        collection(db, COLLECTION_NAME.users)
      );
      let dataList = querySnapshot.docs.map((doc) => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: data.createdAt
            ? new Date(
              data.createdAt.seconds * 1000 +
              data.createdAt.nanoseconds / 1000000
            )
            : null,
          lastLogin: data.lastLogin
            ? new Date(
              data.lastLogin.seconds * 1000 +
              data.lastLogin.nanoseconds / 1000000
            )
            : null,
          lockedUntil: data.lockedUntil
            ? new Date(
              data.lockedUntil.seconds * 1000 +
              data.lockedUntil.nanoseconds / 1000000
            )
            : null,
        };
      });

      dataList = dataList.filter((user) => {
        const hasValidRole = ["Rider", "Driver"].includes(user.role);
        const isApproved = user.documentStatus === "approved";
        const isAvailable =
          !user?.availabilityStatus || user.availabilityStatus === "available";
        const isNotDeactivated =
          !user.accountStatus || user.accountStatus !== "deactivated";
        const isNotActiveRider = !activeRiderIds.includes(user.id);

        // Check vehicle type match if ride has vehicle type requirement
        let hasMatchingVehicle = true;
        if (ride?.vehicleType) {
          console.log("Ride is here:", ride?.vehicleType);
          hasMatchingVehicle =
            user?.vehicles &&
            user?.vehicles?.some((vehicle) => {
              console.log("Ride vehicleType:", ride?.vehicleType);
              console.log("Match:", vehicle?.vehicleType === ride?.vehicleType);
              return vehicle?.vehicleType === ride?.vehicleType;
            });
        }

        return (
          hasValidRole &&
          isApproved &&
          isAvailable &&
          isNotDeactivated &&
          isNotActiveRider &&
          hasMatchingVehicle
        );
      });
      console.log("datalist is here:", dataList);
      setDrivers(dataList);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching drivers:", error);
      setLoading(false);
    }
  }, [fetchActiveRides, ride]);

  const getDriverToken = async (driverId) => {
    return new Promise((resolve) => {
      const driverRef = doc(db, COLLECTION_NAME.users, driverId);
      const unsubscribe = onSnapshot(driverRef, (docSnap) => {
        if (docSnap.exists()) {
          const data = docSnap.data();
          if (data && data.token) {
            unsubscribe(); // Unsubscribe after getting the token
            resolve(data.token);
          } else {
            unsubscribe();
            resolve(null);
          }
        } else {
          unsubscribe();
          resolve(null);
        }
      });
    });
  };

  const sendDriverNotification = async (driverId, driverName) => {
    try {
      const token = await getDriverToken(driverId);

      if (!token) {
        console.log("Driver token not found, skipping notification");
        return;
      }

      const response = await fetch(SEND_NOTIFICATION, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          title: "New Ride Assigned",
          body: `You have been assigned a new ride from admin. Please open app to view ride in Available Rides/Scheduled rides .`,
          token: token,
        }),
      });

      if (response.ok) {
        console.log("Notification sent successfully to driver:", driverName);
      } else {
        console.error("Failed to send notification:", await response.text());
      }
    } catch (error) {
      console.error("Error sending notification:", error);
    }
  };

  const formatRideTime = (timeValue) => {
    if (!timeValue) return "N/A";

    let dateObj;
    if (typeof timeValue === "string") {
      dateObj = new Date(timeValue);
    } else if (timeValue._seconds) {
      dateObj = new Date(timeValue._seconds * 1000);
    } else if (timeValue instanceof Date) {
      dateObj = timeValue;
    } else {
      return "N/A";
    }

    if (isNaN(dateObj.getTime())) return "N/A";

    return dateObj.toLocaleString("en-US", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const fetchDriverScheduledRides = async (driverId) => {
    try {
      const rides = [];

      // Fetch from Realtime Database
      const database = getDatabase();
      const ridesRef = ref(database, COLLECTION_NAME.rides);
      const snapshot = await get(ridesRef);

      if (snapshot.exists()) {
        const ridesData = snapshot.val();
        Object.keys(ridesData).forEach((rideId) => {
          const rideItem = ridesData[rideId];
          if (
            rideItem.riderId === driverId &&
            (rideItem.status === "pending" || rideItem.status === "scheduled")
          ) {
            // Parse createdAt
            let createdAtDate;
            if (rideItem.createdAt && rideItem.createdAt._seconds) {
              createdAtDate = new Date(rideItem.createdAt._seconds * 1000);
            } else if (rideItem.createdAt instanceof Date) {
              createdAtDate = rideItem.createdAt;
            } else {
              createdAtDate = new Date();
            }

            rides.push({
              source: "Realtime Database",
              isScheduled: rideItem.isScheduled || false,
              scheduledTime: rideItem.scheduledTime,
              createdAt: createdAtDate,
              status: rideItem.status,
              pickupAddress: rideItem.pickupAddress || "N/A",
              pickupZone: rideItem.pickupZone || "N/A",
              destinationAddress: rideItem.destinationAddress || "N/A",
              destinationZone: rideItem.destinationZone || "N/A",
            });
          }
        });
      }

      // Fetch from Firestore scheduledRides collection
      const scheduledRidesQuery = query(
        collection(db, COLLECTION_NAME.scheduleRides),
        where("riderId", "==", driverId)
      );
      const scheduledSnapshot = await getDocs(scheduledRidesQuery);

      scheduledSnapshot.forEach((doc) => {
        const rideData = doc.data();
        if (rideData.status === "pending" || rideData.status === "scheduled") {
          // Parse createdAt
          let createdAtDate;
          if (rideData.createdAt && rideData.createdAt._seconds) {
            createdAtDate = new Date(rideData.createdAt._seconds * 1000);
          } else if (rideData.createdAt instanceof Date) {
            createdAtDate = rideData.createdAt;
          } else {
            createdAtDate = new Date();
          }

          rides.push({
            source: `Firestore (${COLLECTION_NAME.scheduleRides})`,
            isScheduled: rideData.isScheduled || false,
            scheduledTime: rideData.scheduledTime,
            createdAt: createdAtDate,
            status: rideData.status,
            pickupAddress: rideData.pickupAddress || "N/A",
            pickupZone: rideData.pickupZone || "N/A",
            destinationAddress: rideData.destinationAddress || "N/A",
            destinationZone: rideData.destinationZone || "N/A",
          });
        }
      });

      return rides;
    } catch (error) {
      console.error("Error fetching driver scheduled rides:", error);
      return [];
    }
  };

  const handleAssign = async () => {
    if (!selectedDriver) return;

    // Check for existing scheduled/pending rides
    setCheckingRides(true);
    const driverRides = await fetchDriverScheduledRides(selectedDriver.id);
    setCheckingRides(false);

    if (driverRides.length > 0) {
      // Show confirmation modal with existing rides
      setExistingRides(driverRides);
      setShowConfirmation(true);
    } else {
      // No existing rides, proceed with assignment
      await proceedWithAssignment();
    }
  };

  const proceedWithAssignment = async () => {
    try {
      // Assign the driver first
      await onVerify(selectedDriver, ride);

      // Then send notification to the driver
      await sendDriverNotification(selectedDriver.id, selectedDriver.fullName);

      setShowConfirmation(false);
      onRequestClose();
    } catch (error) {
      console.error("Error in proceedWithAssignment:", error);
      setShowConfirmation(false);
      onRequestClose();
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchDrivers();
      setSelectedDriver(null);
      setShowConfirmation(false);
      setExistingRides([]);
    }
  }, [isOpen, fetchDrivers]);

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      contentLabel="Assign Driver"
      className="bg-white rounded-lg shadow-xl p-6 max-w-3xl w-full mx-auto mt-24 outline-none"
      overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50"
    >
      <h2 className="text-2xl text-dazzledBlue font-bold mb-2 border-b border-gray-200 pb-2">
        Assign Driver
      </h2>

      {loading ? (
        <div className="flex justify-center items-center h-full p-5">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <input
            type="text"
            placeholder="Search driver by name or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="mb-4 w-full px-4 py-2 border rounded-lg border-gray-300 outline-[#0D3D51]"
          />
          <div className="max-h-[400px] overflow-y-auto pr-2 scrollbar-thin scrollbar-thumb-gray-300">
            {filteredDrivers.map((driver) => (
              <label
                key={driver.id}
                className={`flex items-center space-x-4 p-3 border-b border-gray-200 cursor-pointer hover:bg-gray-50`}
              >
                <div className="flex items-center justify-center">
                  <input
                    type="radio"
                    name="driver"
                    value={driver.id}
                    checked={selectedDriver?.id === driver.id}
                    onChange={() => setSelectedDriver(driver)}
                    className={`appearance-none w-4 h-4 border border-gray-300 rounded-full checked:bg-celadon checked:border-celadon checked:ring-2 checked:ring-offset-2 checked:ring-celadon transition-all duration-200 cursor-pointer`}
                  />
                </div>
                <div className="flex space-x-3 items-start w-full">
                  <img
                    src={driver.profilePicture}
                    alt={driver.fullName}
                    className="w-12 h-12 rounded-full object-cover border border-gray-200"
                  />
                  <div className="flex-1">
                    <p className="font-medium">{driver.fullName}</p>
                    <p className="text-sm text-gray-600">
                      {driver.phoneNumber}
                    </p>
                    {/* Vehicle Type Tags */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {driver.vehicles && driver.vehicles.length > 0 ? (
                        driver.vehicles.map((vehicle, index) => (
                          <span
                            key={index}
                            className={`px-2 py-1 rounded-full text-xs font-medium ${ride?.vehicleType === vehicle?.vehicleType
                                ? "bg-celadon text-primary border border-celadon"
                                : "bg-gray-100 text-gray-600 border border-gray-200"
                              }`}
                          >
                            {vehicle?.vehicleType || "Unknown"}
                          </span>
                        ))
                      ) : (
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500 border border-gray-200">
                          No Vehicle Info
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </label>
            ))}
          </div>
        </>
      )}

      <div className="mt-4 flex justify-end space-x-2">
        <button
          onClick={onRequestClose}
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
        >
          Cancel
        </button>
        <button
          onClick={handleAssign}
          disabled={(!selectedDriver || checkingRides) && !isAssigning}
          className={`px-4 py-2 rounded ${selectedDriver && !checkingRides
              ? "bg-celadon text-primary"
              : "bg-gray-300 text-gray-500 cursor-not-allowed"
            }`}
        >
          {checkingRides
            ? "Checking..."
            : !isAssigning
              ? "Assign Driver"
              : "Assigning..."}
        </button>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-[80vh] overflow-y-auto">
            <h3 className="text-xl font-bold text-red-600 mb-4">
              Warning: Driver has existing rides
            </h3>
            <p className="mb-4 text-gray-700">
              <strong>{selectedDriver?.fullName}</strong> has{" "}
              <strong>{existingRides.length}</strong> pending/scheduled ride
              {existingRides.length > 1 ? "s" : ""}:
            </p>
            <div className="mb-4 max-h-[300px] overflow-y-auto">
              {existingRides.map((ride, index) => (
                <div
                  key={index}
                  className="border border-gray-300 rounded-lg p-4 mb-3 bg-gray-50"
                >
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <p className="font-semibold text-gray-800">
                        Ride #{index + 1}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${ride.status === "pending"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-blue-100 text-blue-800"
                        }`}
                    >
                      {ride.status.toUpperCase()}
                    </span>
                  </div>
                  <div className="space-y-2 text-sm">
                    {ride.isScheduled && ride.scheduledTime && (
                      <div>
                        <p className="text-gray-700">
                          <strong>Scheduled Time:</strong>{" "}
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary">
                            {formatRideTime(ride.scheduledTime)}
                          </span>
                        </p>
                      </div>
                    )}
                    {!ride.isScheduled && (
                      <div>
                        <p className="text-gray-700">
                          <strong>Ride Type:</strong>{" "}
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary">
                            Normal Ride
                          </span>
                        </p>
                      </div>
                    )}
                    <div>
                      <p className="text-gray-700">
                        <strong>Created At:</strong>{" "}
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-celadon text-primary">
                          {formatRideTime(ride.createdAt)}
                        </span>
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">
                        <strong>Pickup Address:</strong> {ride.pickupAddress}
                      </p>
                      <p className="text-gray-700">
                        <strong>Pickup Zone:</strong> {ride.pickupZone}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-700">
                        <strong>Destination Address:</strong>{" "}
                        {ride.destinationAddress}
                      </p>
                      <p className="text-gray-700">
                        <strong>Destination Zone:</strong>{" "}
                        {ride.destinationZone}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <p className="mb-6 text-gray-700 font-medium">
              Are you sure you want to assign this ride to this driver?
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowConfirmation(false)}
                className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={proceedWithAssignment}
                disabled={isAssigning}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
              >
                {isAssigning ? "Assigning..." : "Yes, Assign Anyway"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AssignDriverModal;

